import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import { CustomDialogContainer } from '../../Common/dialog';
import { axiosInstance } from '../../utils';
import { Iconify } from '@sdf/base';
import { useNotification } from '../../hooks';
import { useGoogleLoginHandler } from '@t2c/auth';
import { palette, style } from '@mui/system';
import { profileStyle as pstyle } from 'apps/t2c-fe/styles/profle.style';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function GuestVerifyModal({ open, onClose }: Props) {
  const { showNotification } = useNotification();
  const [email, setEmail] = useState('');
  const [initialEmail, setInitialEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [showGmailConfirm, setShowGmailConfirm] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    const run = async () => {
      try {
        const res = await axiosInstance.get('/api/users/get-user');
        setEmail(res.data?.email || '');
        setInitialEmail(res.data?.email || '');
        setIsGuest(!!res.data?.isGuest);
      } catch {}
    };
    run();
  }, [open]);

  const handleSaveEmail = async () => {
    setSaving(true);
    try {
      if (email && email !== initialEmail) {
        await axiosInstance.patch('/api/users/update-user', { email });
        setInitialEmail(email);
      }
      setIsEditingEmail(false);
    } catch (e: any) {
      showNotification('Failed to update email', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendPassword = async () => {
    setSending(true);
    // Auto-save email if changed
    if (email !== initialEmail) {
      try {
        await axiosInstance.patch('/api/users/update-user', { email });
        setInitialEmail(email);
      } catch {
        showNotification('Failed to update email before sending', 'error');
        setSending(false);
        return;
      }
    }
    try {
      await axiosInstance.post('/api/auth/send-recovery');
      setSent(true);
    } catch {
      showNotification('Failed to send password setup link. Please try again.', 'error');
    } finally {
      setSending(false);
    }
  };

  const { handleLogin } = useGoogleLoginHandler({
    onSuccess: () => {
      // on success, set isGuest to false and close; parent should refresh auth context
      setIsGuest(false);
      onClose();
    },
    onError: () => {
      setShowGmailConfirm(false);
    },
  });

  const handleGmailLogin = useCallback(() => {
    setShowGmailConfirm(true);
  }, []);

  const handleConfirmGmail = useCallback(async () => {
    // Ensure email looks like a gmail address (basic heuristic) but allow proceeding anyway
    setConfirming(true);
    try {
      // Persist any unsaved email change first
      if (email && email !== initialEmail) {
        try {
          await axiosInstance.patch('/api/users/update-user', { email });
          setInitialEmail(email);
        } catch {
          showNotification('Failed to update email before Google login', 'error');
          setConfirming(false);
          return;
        }
      }
      // Launch Google login popup
      await handleLogin();
    } finally {
      setConfirming(false);
    }
  }, [email, initialEmail, handleLogin, showNotification]);

  if (!isGuest) return null;

  return (
    <CustomDialogContainer open={open} onClose={onClose}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, position: 'relative', pr: 5 }}>
        {sent ? 'Password link sent' : 'Set up your password'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.text.primary,
          }}
        >
          <Iconify icon="mingcute:close-line" width={18} height={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: (theme) => theme.palette.background.secondary,
          textAlign: 'center',
          py: 3,
        }}
      >
        <Typography
          variant="body1"
          sx={{ mb: 2, mt: 1, color: (theme) => theme.palette.text.lighterText2 }}
        >
          {showGmailConfirm
            ? `Please confirm that ${email} is a Google (Gmail) account you currently have access to. If it is incorrect, update it below before proceeding.`
            : sent
            ? `A password setup link has been sent to ${email}. It expires in 24 hours.`
            : `Send a password setup link to ${email} to secure your account. If you haven't received the email, update your email address below or resend the link.`}
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          {isEditingEmail ? (
            <TextField
              size="small"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              sx={{ maxWidth: 300 }}
            />
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1">{email}</Typography>
              <Button
                variant="contained"
                onClick={() => setIsEditingEmail(true)}
                sx={{ minWidth: 'auto', p: 1, color: 'white', ...pstyle.editBtn }}
              >
                <Iconify icon="lucide:edit" height={20} width={20} />
              </Button>
            </Box>
          )}
        </Box>
        {isEditingEmail && (
          <Box display="flex" gap={1} justifyContent="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              sx={{ color: 'white', ...pstyle.btns }}
              onClick={handleSaveEmail}
              disabled={saving}
            >
              {saving ? 'Updating…' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              sx={{ color: 'white', ...pstyle.btns }}
              onClick={() => {
                setEmail(initialEmail);
                setIsEditingEmail(false);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
        <Box display="flex" gap={1} justifyContent="center" alignItems="center">
          {showGmailConfirm ? (
            <>
              <Button
                sx={{ boxShadow: 'none', color: 'white' }}
                variant="outlined"
                onClick={() => setShowGmailConfirm(false)}
                disabled={confirming}
              >
                Not Google Account
              </Button>
              <Button
                sx={{ boxShadow: 'none', color: 'white' }}
                variant="contained"
                onClick={handleConfirmGmail}
                disabled={confirming}
              >
                {confirming ? 'Opening…' : 'Confirm & Continue'}
              </Button>
            </>
          ) : sent ? (
            <>
              <Button
                sx={{ boxShadow: 'none', color: 'white' }}
                variant="outlined"
                onClick={() => (window.location.href = 'mailto:')}
              >
                Open email app
              </Button>
              <Button
                sx={{ boxShadow: 'none', color: 'white' }}
                variant="contained"
                onClick={handleSendPassword}
                disabled={sending}
              >
                {sending ? 'Sending…' : 'Resend Email'}
              </Button>
            </>
          ) : (
            <>
              <Button
                sx={{ boxShadow: 'none', color: 'white' }}
                variant="contained"
                onClick={handleSendPassword}
                disabled={sending}
              >
                {sending ? 'Sending…' : 'Email Password Setup'}
              </Button>
              <Button
                sx={{ boxShadow: 'none', color: 'white' }}
                variant="contained"
                onClick={handleGmailLogin}
                disabled={sending}
              >
                {sending ? '...' : 'Use Google Login'}
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
    </CustomDialogContainer>
  );
}
